import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ChevronRight, Calendar, Clock, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get current month name
const getCurrentMonth = () => {
  return new Date().toLocaleString('default', { month: 'long' });
};

// Exam Card Component
const ExamCard = ({ exam, onFilter }) => {
  const isPrepping = exam.prep_now;
  const isExamNow = exam.exam_now;
  
  return (
    <Card 
      className={`group hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden ${
        isExamNow ? 'ring-2 ring-red-500 ring-offset-2' : 
        isPrepping ? 'ring-2 ring-amber-500 ring-offset-2' : ''
      }`}
      onClick={() => onFilter && onFilter(exam)}
      data-testid={`exam-card-${exam.id}`}
    >
      {/* Image Header */}
      <div className="relative h-28 overflow-hidden">
        <img 
          src={exam.image_url} 
          alt={exam.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        
        {/* Status Badge */}
        <div className="absolute top-2 right-2">
          {isExamNow ? (
            <Badge className="bg-red-500 text-white text-xs animate-pulse">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Exam Now!
            </Badge>
          ) : isPrepping ? (
            <Badge className="bg-amber-500 text-white text-xs">
              <Clock className="w-3 h-3 mr-1" />
              Prep Time
            </Badge>
          ) : (
            <Badge variant="secondary" className="bg-white/90 text-xs">
              {exam.start_month}
            </Badge>
          )}
        </div>
        
        {/* Icon */}
        <div className="absolute bottom-2 left-2 text-2xl drop-shadow-lg">
          {exam.icon}
        </div>
        
        {/* Title on image */}
        <div className="absolute bottom-2 left-10 right-2">
          <h3 className="font-semibold text-white text-sm line-clamp-1 drop-shadow">
            {exam.title}
          </h3>
        </div>
      </div>
      
      {/* Content */}
      <CardContent className="p-2.5">
        <p className="text-xs text-gray-500 line-clamp-2 mb-2">{exam.description}</p>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <Calendar className="w-3 h-3" />
            <span>{exam.start_month}{exam.end_month !== exam.start_month ? ` - ${exam.end_month}` : ''}</span>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-indigo-600 transition-colors" />
        </div>
      </CardContent>
    </Card>
  );
};

export default function ExamCalendarSection({ 
  country = 'UK', 
  onFilterByExam,
  className = '' 
}) {
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchExamCalendar = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${API_URL}/api/education-tuition/exam-calendar/${country}`
        );
        const data = await response.json();
        
        if (data.success) {
          setCalendarData(data);
        }
      } catch (error) {
        console.error('Failed to fetch exam calendar:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchExamCalendar();
  }, [country]);

  const handleFilter = (exam) => {
    if (onFilterByExam) {
      onFilterByExam(exam.subcategory_filter, exam.exam_type);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center gap-2 mb-4">
          <div className="h-6 w-48 bg-gray-200 animate-pulse rounded" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-40 bg-gray-100 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!calendarData?.enabled || !calendarData?.exams?.length) {
    return null;
  }

  // Sort: exams happening now first, then prep time, then by display order
  const sortedExams = [...calendarData.exams].sort((a, b) => {
    if (a.exam_now && !b.exam_now) return -1;
    if (!a.exam_now && b.exam_now) return 1;
    if (a.prep_now && !b.prep_now) return -1;
    if (!a.prep_now && b.prep_now) return 1;
    return a.display_order - b.display_order;
  });

  const displayExams = showAll ? sortedExams : sortedExams.slice(0, 4);
  const prepNowCount = sortedExams.filter(e => e.prep_now).length;
  const examNowCount = sortedExams.filter(e => e.exam_now).length;

  return (
    <div className={`${className}`} data-testid="exam-calendar-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Calendar className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              {calendarData.section_title}
              {examNowCount > 0 && (
                <Badge className="bg-red-100 text-red-700 text-xs">
                  {examNowCount} happening now
                </Badge>
              )}
              {examNowCount === 0 && prepNowCount > 0 && (
                <Badge className="bg-amber-100 text-amber-700 text-xs">
                  {prepNowCount} in prep window
                </Badge>
              )}
            </h2>
            <p className="text-xs text-gray-500">{calendarData.section_subtitle}</p>
          </div>
        </div>
        
        {sortedExams.length > 4 && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setShowAll(!showAll)}
            className="text-indigo-600 hover:text-indigo-700 text-sm"
          >
            {showAll ? 'Show Less' : `View All (${sortedExams.length})`}
          </Button>
        )}
      </div>

      {/* Exams Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {displayExams.map((exam) => (
          <ExamCard 
            key={exam.id}
            exam={exam}
            onFilter={handleFilter}
          />
        ))}
      </div>
    </div>
  );
}
